'use client'
import {
  Input_default
} from "./chunk-IC7RIEVI.mjs";
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/Select/Select.tsx
import { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole
} from "@floating-ui/react";
import { Fragment as Fragment2, jsx, jsxs } from "react/jsx-runtime";
var Select = (_a) => {
  var _b = _a, {
    label,
    onChange,
    error,
    rounded = true,
    options,
    className,
    optionsClassName,
    autoSelect,
    value,
    hideErrorSpace = true,
    emptyText = "No options found",
    groupClassName,
    renderOption,
    renderLabel,
    positionRef,
    portal = true,
    ref
  } = _b, props = __objRest(_b, [
    "label",
    "onChange",
    "error",
    "rounded",
    "options",
    "className",
    "optionsClassName",
    "autoSelect",
    "value",
    "hideErrorSpace",
    "emptyText",
    "groupClassName",
    "renderOption",
    "renderLabel",
    "positionRef",
    "portal",
    "ref"
  ]);
  var _a2, _b2, _c, _d;
  const MenuContainer = portal ? FloatingPortal : Fragment;
  const selectedOption = useMemo(() => {
    const current = value;
    if (current == null ? void 0 : current.value) {
      const index2 = options.findIndex((o) => o.value === current.value);
      return {
        option: current,
        index: index2
      };
    }
    const index = options.findIndex((o) => o.value === current);
    return {
      option: options[index],
      index
    };
  }, [value, options]);
  const selectedLabel = selectedOption.option ? (renderLabel == null ? void 0 : renderLabel(selectedOption.option)) || ((_a2 = selectedOption == null ? void 0 : selectedOption.option) == null ? void 0 : _a2.text) || ((_b2 = selectedOption == null ? void 0 : selectedOption.option) == null ? void 0 : _b2.value) : "";
  const [query, setQuery] = useState(selectedLabel);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(selectedOption.index);
  const filteredOptions = useMemo(() => {
    if (query === selectedLabel)
      return options;
    return options.filter(
      (o) => ((o == null ? void 0 : o.text) || "").toLowerCase().includes(query.toLowerCase()) || o.value.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, options, selectedLabel]);
  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxWidth: `${rects.reference.width}px`
          });
        },
        padding: 10
      })
    ]
  });
  const handleSelect = (option) => {
    onChange == null ? void 0 : onChange(option);
    setQuery((renderLabel == null ? void 0 : renderLabel(option)) || option.text || option.value);
    setIsOpen(false);
  };
  const handleChange = (value2) => {
    setQuery(value2);
    if (value2) {
      setIsOpen(true);
      setActiveIndex(0);
    } else {
      setIsOpen(false);
    }
  };
  useImperativeHandle(ref, () => refs.domReference.current);
  useEffect(() => {
    if (positionRef == null ? void 0 : positionRef.current) {
      refs.setPositionReference(positionRef.current);
    }
  }, [positionRef, refs]);
  const listRef = useRef([]);
  const dismiss = useDismiss(context, {
    outsidePress: () => {
      setQuery(selectedLabel);
      return true;
    }
  });
  const role = useRole(context, { role: "listbox" });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex: options.findIndex((o) => {
      var _a3;
      return o.value === ((_a3 = selectedOption == null ? void 0 : selectedOption.option) == null ? void 0 : _a3.value);
    }),
    onNavigate: setActiveIndex,
    loop: true,
    virtual: true
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    dismiss,
    role,
    listNav
  ]);
  return /* @__PURE__ */ jsxs("div", { className: groupClassName, children: [
    label && /* @__PURE__ */ jsx(
      "label",
      {
        className: `block pb-1 font-semibold ${props.disabled ? "text-text-secondary" : ""}`,
        htmlFor: props.id,
        role: "presentation",
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        children: label
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(
        Input_default,
        __spreadValues(__spreadValues({
          ref: refs.setReference,
          "aria-labelledby": "select-label",
          "aria-autocomplete": "none",
          className: twMerge(((_c = selectedOption == null ? void 0 : selectedOption.option) == null ? void 0 : _c.icon) && "ltr:pl-8 rtl:pr-8", className),
          value: query,
          onChange: handleChange,
          error,
          rounded,
          hideErrorSpace
        }, props), getReferenceProps({
          onKeyDown: (event) => {
            if (event.key === "Enter" && !isOpen)
              return setIsOpen(true);
            const selected = typeof activeIndex === "number" && filteredOptions[activeIndex];
            if (event.key === "Enter" && selected) {
              event.preventDefault();
              handleSelect(selected);
            }
          },
          onClick() {
            setIsOpen(true);
          },
          onFocus(e) {
            if (autoSelect) {
              e.target.select();
            }
          }
        }))
      ),
      ((_d = selectedOption == null ? void 0 : selectedOption.option) == null ? void 0 : _d.icon) && /* @__PURE__ */ jsx("span", { className: "absolute top-0 flex h-[42px] items-center px-2 disabled:cursor-not-allowed ltr:left-0 rtl:right-0", children: selectedOption == null ? void 0 : selectedOption.option.icon }),
      /* @__PURE__ */ jsx(
        "button",
        {
          className: `absolute top-0 h-[42px] px-2 disabled:cursor-not-allowed ${error ? "ltr:right-3 rtl:left-3" : "ltr:right-0 rtl:left-0"}`,
          "aria-label": `Open ${props.name}`,
          onClick: () => {
            setIsOpen(true);
          },
          tabIndex: -1,
          disabled: props.disabled,
          type: "button",
          children: /* @__PURE__ */ jsx("svg", { width: "22", height: "22", viewBox: "0 0 24 24", className: "fill-text/50 rotate-180", children: /* @__PURE__ */ jsx("path", { d: "M6.70710678,15.7071081 C6.31658249,16.0976324 5.68341751,16.0976324 5.29289322,15.7071081 C4.90236893,15.3165838 4.90236893,14.6834188 5.29289322,14.2928945 L11.2928932,8.29289454 C11.6714722,7.9143156 12.2810586,7.90106998 12.6757246,8.26284718 L19.4313874,14.2942293 C19.8385064,14.6674217 19.8660094,15.2999891 19.4928169,15.7071081 C19.1196245,16.1142271 18.4870572,16.14173 18.0799382,15.7685376 L12.0300757,10.3841391 L6.70710678,15.7071081 Z" }) })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(MenuContainer, { children: isOpen && /* @__PURE__ */ jsx(FloatingFocusManager, { context, initialFocus: -1, visuallyHiddenDismiss: true, children: /* @__PURE__ */ jsxs(
      "div",
      __spreadProps(__spreadValues({
        ref: refs.setFloating,
        className: twMerge(
          "mini-scrollbar bg-input-popover border-input-border text-text rounded-S isolation-auto z-[99] max-h-40 overflow-y-auto border py-1 shadow-lg focus:outline-none sm:text-sm md:max-h-52",
          optionsClassName
        ),
        style: __spreadProps(__spreadValues({}, floatingStyles), {
          // added to fix blurred text on low end devices when selected is rendered in a modal
          position: portal ? floatingStyles.position : "fixed"
        })
      }, getFloatingProps()), {
        children: [
          filteredOptions.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-text-secondary p-4 text-center", children: emptyText }),
          filteredOptions.map((option, i) => {
            var _a3;
            return /* @__PURE__ */ jsx(
              "div",
              __spreadProps(__spreadValues({
                ref: (node) => {
                  listRef.current[i] = node;
                },
                role: "option",
                tabIndex: i === activeIndex ? 0 : -1,
                "aria-selected": i === activeIndex,
                className: `flex cursor-pointer select-none items-center space-x-2 px-4 py-2 outline-none rtl:space-x-reverse ${i === activeIndex ? "bg-primary-400 text-primary-contrast" : ""}`
              }, getItemProps({
                onClick() {
                  var _a4;
                  handleSelect(option);
                  (_a4 = refs.domReference.current) == null ? void 0 : _a4.focus();
                }
              })), {
                children: renderOption ? renderOption(option) : /* @__PURE__ */ jsxs(Fragment2, { children: [
                  option.icon && option.icon,
                  /* @__PURE__ */ jsx(
                    "span",
                    {
                      className: `block truncate ${((_a3 = selectedOption == null ? void 0 : selectedOption.option) == null ? void 0 : _a3.value) === option.value ? "font-medium" : "font-normal"}`,
                      children: option.text || option.value
                    }
                  )
                ] })
              }),
              option.value
            );
          })
        ]
      })
    ) }) })
  ] });
};
var Select_default = Select;

export {
  Select_default
};
//# sourceMappingURL=chunk-BHYWQV4K.mjs.map